import { NotificationInfoOutlineIconRaw } from '@tableau/icons/NotificationInfoOutlineIcon/raw';
import { Colors } from '@tableau/icons/util/Colors';
import { TextLinkWidget, Tooltip } from '@tableau/widgets';
import React from 'react';
import { classes, style } from 'typestyle';

import { SessionServerSelectors, SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { Messages } from '~src/codegen/Localize';
import { FormikCheckbox } from '~src/commonInfra/components/FormikCheckbox';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { useSelector } from '~src/settings/general-settings/utils';
import { learnMoreStyle,SettingsStyles } from '~src/settings/styles/CommonStyles';

import { SettingsSection } from '../../../../components/common/SettingsSection';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import { SiteSettingsForSiteAdminProps } from '../../site-settings/ReactSiteSettingsForSiteAdmin';


export const GROUP_ASSERTIONS_SITE_ENABLED_FIELD = 'settings.groupAssertionsEnabled';
export const GROUP_ASSERTIONS_SAML_SITE_ENABLED_FIELD = 'settings.groupAssertionsSAMLEnabled';
export const GROUP_ASSERTIONS_OIDC_SITE_ENABLED_FIELD = 'settings.groupAssertionsOIDCEnabled';
export const GROUP_ASSERTIONS_CONNECTED_APPS_SITE_ENABLED_FIELD = 'settings.groupAssertionsConnectedAppsEnabled';
export const GROUP_ASSERTIONS_SITE_SECTION_TEST_ID = 'group-assertions-site-setting-section';
export const GROUP_ASSERTIONS_SITE_ENABLED_CHECKBOX_TEST_ID = 'group-assertions-site-enabled';
export const GROUP_ASSERTIONS_SITE_LEARN_MORE_LINK_TEST_ID = 'group-assertions-learn-more-link';

export type SiteGroupAssertionsSectionProps = SiteSettingsForSiteAdminProps<'groupAssertionsEnabled' | 'groupAssertionsSAMLEnabled' |
 'groupAssertionsOIDCEnabled' | 'groupAssertionsConnectedAppsEnabled'>;

 const tooltipStyle = style({
    display: 'flex',
    alignItems: 'center',
  });

  const smallPaddingStyle = style({
    paddingLeft: 3,
    display: 'inline-block'
});

export function SiteGroupAssertionsSection(props: SiteGroupAssertionsSectionProps): React.ReactElement {
    const isGloballyEnabled = useSelector(SessionServerSelectors.isGroupAssertionsEnabledOnServer);
    const isOnlineEnvironment = useSelector(SessionServerSelectors.isOnlineEnvironment);
    const isSiteAdmin = useSelector(SessionSiteSelectors.isSessionSiteAdmin);
    const isAllowedToChangeSetting = isGloballyEnabled || (isOnlineEnvironment && isSiteAdmin);

    const showGroupAssertionsControl = FeatureFlags.abacassertedLocalGroupsEnabled;
    if (!showGroupAssertionsControl) {
        return null;
    }

    return (
        <SettingsSection
                description={renderDescription(isOnlineEnvironment)}
                title={Messages.groupAssertionsSettingTitle()}
                testId={GROUP_ASSERTIONS_SITE_SECTION_TEST_ID}>
                    <div className={classes(SettingsStyles.checkboxGroup, tooltipStyle)}>
                            <div>
                                <FormikCheckbox
                                    label={renderSettingLabel(isOnlineEnvironment)}
                                    name={GROUP_ASSERTIONS_SITE_ENABLED_FIELD}
                                    testId={GROUP_ASSERTIONS_SITE_ENABLED_CHECKBOX_TEST_ID}
                                    disabled={!isAllowedToChangeSetting}
                                    checked={!isAllowedToChangeSetting ? false : undefined}>
                                    <div className={SettingsStyles.checkboxGroup}>
                                        <div>
                                            <FormikCheckbox name={GROUP_ASSERTIONS_SAML_SITE_ENABLED_FIELD}
                                                label={Messages.groupAssertionsSAMLSettingLabel()}
                                                testId={GROUP_ASSERTIONS_SITE_ENABLED_CHECKBOX_TEST_ID+'-saml'}
                                                childrenSkipIndent={true}
                                                alwaysShowChildren={true}
                                                />
                                            {renderOIDCCheckbox(isOnlineEnvironment)}
                                            <FormikCheckbox name={GROUP_ASSERTIONS_CONNECTED_APPS_SITE_ENABLED_FIELD}
                                                label={Messages.groupAssertionsConnectedAppsSettingLabel()}
                                                testId={GROUP_ASSERTIONS_SITE_ENABLED_CHECKBOX_TEST_ID+'-connected-apps'}
                                                childrenSkipIndent={true}
                                                alwaysShowChildren={true}
                                                />
                                        </div>
                                    </div>
                                </FormikCheckbox>
                            </div>
                            <div>
                                {!isAllowedToChangeSetting &&
                                <Tooltip
                                    testId={'group-assertions-site-control-tooltip-id'}
                                    tooltipContent={<span>{Messages.groupAssertionsInfoTip()}</span>}
                                    >
                                <img
                                className={smallPaddingStyle}
                                data-tb-test-id={'group-assertion-control-tooltip-icon-id'}
                                src={`data:image/svg+xml,${encodeURIComponent(NotificationInfoOutlineIconRaw(16, Colors.Default))}`}
                                alt={Messages.groupSet()}
                                />
                                </Tooltip>
                                }
                            </div>
                        </div>
            </SettingsSection>
    );
}

function renderOIDCCheckbox(isOnline: boolean) {
    if (isOnline) {
        return <FormikCheckbox name={GROUP_ASSERTIONS_OIDC_SITE_ENABLED_FIELD}
                                        label={Messages.groupAssertionsOIDCSettingLabel()}
                                        testId={GROUP_ASSERTIONS_SITE_ENABLED_CHECKBOX_TEST_ID+'-oidc'}
                                        childrenSkipIndent={true}
                                        alwaysShowChildren={true}
                                        />;
    }
}

function renderSettingLabel(isOnline: boolean) {
    if (isOnline) {
        return Messages.groupAssertionsCloudSettingLabel();
    } else {
        return Messages.groupAssertionsOnPremSettingLabel();
    }
}

function renderDescription(isOnline: boolean) {
    var settingDescription;
    if (isOnline) {
        settingDescription = Messages.groupAssertionsSiteSettingCloudDescription();
    } else {
        settingDescription = Messages.groupAssertionsSiteSettingOnPremDescription();
    }
    return (
        <div>
            {settingDescription}
            <TextLinkWidget testId={GROUP_ASSERTIONS_SITE_LEARN_MORE_LINK_TEST_ID}
                            target={'_blank'}
                            className={learnMoreStyle}
                            href={HelpService.getHelpUrlByCategory('group-assertions')}>
                {Messages.learnMore()}
            </TextLinkWidget>
        </div>
    );

}

/* for registering the component in settings registry */
export const SiteGroupAssertionsSectionSpecs = componentRegistryEntry({
    name: 'SiteGroupAssertionsSection',
    component: SiteGroupAssertionsSection
});
